import KioskReceipt from "../KioskReceipt";
import axios from "axios";
import { useCallback, useEffect, useRef, useState } from "react";
import { Alert, Box, Button, CircularProgress, Dialog, Stack, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { terminalCall, workstationId } from "./useTerminalCheckout";

const kioskCall = async (method, args) => (await axios.post(`method/restaurant_management.terminal_payments.kiosk.${method}`, args, { timeout: 30000 })).data.message;
const copy = {
    en: { title: "Pay by card", preparing: "Preparing your order…", Prepared: "Your order is ready for payment", Processing: "Follow the instructions on the card terminal", Unknown: "We are checking your payment. Please do not pay again. Ask a member of staff for help.", Declined: "Payment was not approved. You can try again or return to your order.", Paid: "Payment approved. Completing your order…", "Invoice Pending": "Payment approved. Please ask a member of staff to help complete your order. Do not pay again.", Completed: "Thank you! Your order has been sent to the kitchen.", Voiding: "Checking your payment. Please ask a member of staff for help.", pay: "Pay now", retry: "Try again", finish: "Complete order", back: "Return to order", next: "Start a new order", order: "Order number", unavailable: "Card payment is unavailable at this kiosk. Please place your order and pay by cash or card at the cashier.", connection: "Unable to confirm the payment status. Please check again or ask a member of staff. Do not pay again.", check: "Check status" },
    ar: { title: "الدفع بالبطاقة", preparing: "جارٍ تجهيز طلبك…", Prepared: "طلبك جاهز للدفع", Processing: "اتبع التعليمات على جهاز الدفع", Unknown: "جارٍ التحقق من الدفع. لا تدفع مرة أخرى. اطلب المساعدة من الموظف.", Declined: "لم يتم قبول الدفع. يمكنك المحاولة مجدداً أو العودة إلى طلبك.", Paid: "تم قبول الدفع. جارٍ إكمال طلبك…", "Invoice Pending": "تم قبول الدفع. اطلب من الموظف المساعدة لإكمال طلبك. لا تدفع مرة أخرى.", Completed: "شكراً لك! تم إرسال طلبك إلى المطبخ.", Voiding: "جارٍ التحقق من الدفع. اطلب المساعدة من الموظف.", pay: "ادفع الآن", retry: "حاول مجدداً", finish: "إكمال الطلب", back: "العودة إلى الطلب", next: "بدء طلب جديد", order: "رقم الطلب", unavailable: "الدفع بالبطاقة غير متاح على هذا الجهاز. يرجى إتمام الطلب والدفع نقداً أو بالبطاقة لدى الكاشير.", connection: "تعذّر تأكيد حالة الدفع. تحقّق مجدداً أو اطلب المساعدة. لا تدفع مرة أخرى.", check: "التحقق من الحالة" },
};

// Kept mounted throughout kiosk usage: recovery runs even when the cart is empty.
export default function KioskCheckout({ printTransport, profile, items, requested, onClosed, onCompleted }) {
    const { i18n } = useTranslation();
    const w = copy[i18n.language?.startsWith("ar") ? "ar" : "en"];
    const [workstation] = useState(workstationId);
    const [session, setSession] = useState(null);
    const [checking, setChecking] = useState(true);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState("");
    const [unavailable, setUnavailable] = useState(false);
    const locked = useRef(false);
    const sessionRef = useRef(null);
    const requestKey = `cayenne.kiosk.checkout.${profile}`;
    const update = useCallback(next => { sessionRef.current = next; setSession(next); }, []);

    const recover = useCallback(async () => {
        const pending = await kioskCall("resume", { pos_profile: profile, workstation_id: workstation });
        update(pending);
        setChecking(false);
        setError("");
        return pending;
    }, [profile, workstation, update]);
    useEffect(() => {
        let active = true;
        setChecking(true);
        kioskCall("resume", { pos_profile: profile, workstation_id: workstation })
            .then(pending => { if (active) { update(pending); setChecking(false); } })
            .catch(() => { if (active) setError("connection"); });
        return () => { active = false; };
    }, [profile, workstation, update]);

    const run = useCallback(async (task, { background = false } = {}) => {
        if (locked.current) return;
        locked.current = true;
        if (!background) setBusy(true);
        setError("");
        try { return await task(); }
        catch (failure) {
            // A rejected preparation has not charged anything. Verify recovery
            // before allowing cash fallback; transport failures stay blocked.
            if (!sessionRef.current && failure?.response?.status < 500) {
                try {
                    if (!(await recover())) setUnavailable(true);
                } catch (_) { setError("connection"); }
            } else setError("connection");
        }
        finally { locked.current = false; if (!background) setBusy(false); }
    }, [recover]);
    const refresh = () => run(async () => {
        if (sessionRef.current) update(await terminalCall("status", { checkout: sessionRef.current.name }));
        else await recover();
    });
    useEffect(() => {
        if (!session?.name || !["Processing", "Unknown", "Voiding", "Invoice Pending"].includes(session.status)) return;
        const timer = setInterval(() => {
            if (locked.current) return;
            // Publish the result after releasing the request lock, so Paid can
            // immediately trigger finalization without racing the status request.
            run(() => terminalCall("status", { checkout: sessionRef.current.name }), { background: true })
                .then(next => { if (next) update(next); });
        }, 2000);
        return () => clearInterval(timer);
    }, [session?.name, session?.status, run, update]);

    useEffect(() => {
        if (!requested || checking || session || unavailable || error || locked.current) return;
        run(async () => {
            if (await recover()) return;
            const config = await terminalCall("configuration", { pos_profile: profile, workstation_id: workstation });
            const mode = Object.keys(config.methods).find(name => config.methods[name] && config.terminals.some(t => t.provider === config.methods[name] && t.online));
            if (!mode) { setUnavailable(true); return; }
            let request = localStorage.getItem(requestKey);
            if (!request) { request = window.crypto.randomUUID(); localStorage.setItem(requestKey, request); }
            // Preparation does not charge. Show the authoritative amount before Pay now.
            update(await kioskCall("prepare", { pos_profile: profile, workstation_id: workstation, request_id: request, items, mode_of_payment: mode }));
        });
    }, [requested, checking, session, unavailable, error, profile, workstation, requestKey, items, recover, run, update]);

    useEffect(() => {
        if (session?.status === "Paid") {
            run(async () => update(await terminalCall("finalize", { checkout: session.name })));
        }
    }, [session?.name, session?.status, run, update]);

    const act = method => run(async () => update(await terminalCall(method, { checkout: session.name })));
    const leave = () => run(async () => {
        if (session && !["Completed", "Cancelled"].includes(session.status)) {
            update(await terminalCall("cancel", { checkout: session.name }));
        }
        if (sessionRef.current) await kioskCall("acknowledge", { checkout: sessionRef.current.name });
        const completed = sessionRef.current?.status === "Completed";
        localStorage.removeItem(requestKey);
        update(null);
        setUnavailable(false);
        onClosed();
        if (completed) onCompleted();
    });
    const safeToLeave = !checking && !error && (!session || ["Prepared", "Declined", "Cancelled", "Completed"].includes(session.status));
    return <Dialog open={Boolean(requested || session || error)} disableEscapeKeyDown maxWidth="sm" fullWidth>
        <Box p={4} dir={i18n.dir()} sx={{ minHeight: 420 }}>
            <Stack spacing={3} alignItems="center" textAlign="center">
                <Typography variant="h4">{w.title}</Typography>
                <Box sx={{ height: 40, display: "flex", alignItems: "center", justifyContent: "center" }}>
                    {(checking || busy || session?.status === "Processing") && <CircularProgress />}
                </Box>
                <Typography variant="h5" role="status" aria-live="polite">{unavailable ? w.unavailable : session ? w[session.status] : w.preparing}</Typography>
                {session && <Typography variant="h4">{(Number(session.total_minor) / 100).toFixed(2)} {session.currency}</Typography>}
                {session && <Typography>{w.order}: <bdi>{session.table_order}</bdi></Typography>}
                {error && <Alert severity="warning">{w.connection}</Alert>}
                {["Prepared", "Declined"].includes(session?.status) && <Button size="large" variant="contained" disabled={busy || Boolean(error)} onClick={() => act("start")}>{session.status === "Declined" ? w.retry : w.pay}</Button>}
                {["Paid", "Invoice Pending"].includes(session?.status) && <Button size="large" variant="contained" disabled={busy} onClick={() => act("finalize")}>{w.finish}</Button>}
                {(error || session?.status === "Unknown") && <Button size="large" disabled={busy} onClick={refresh}>{w.check}</Button>}
                {session?.status === "Completed" && session.invoice && <KioskReceipt transport={printTransport} doctype="POS Invoice" name={session.invoice} />}
                {safeToLeave && <Button size="large" disabled={busy} onClick={leave}>{session?.status === "Completed" ? w.next : w.back}</Button>}
            </Stack>
        </Box>
    </Dialog>;
}
